import { ReactElement } from 'react';
import SchemaOrg from './SchemaOrg';
import { convertTime12to24, structuredPhoneNumber } from 'lib/util';
import { IPageLocationFields, IPageDoctor } from 'types/contentful';

interface MedicalBusinessSchemaProps {
  location: IPageLocationFields;
  siteName: string;
  domain: string;
  paymentsAccepted?: string[];
  areaServed?: string[];
  doctors?: IPageDoctor[];
  hasProducts?: boolean;
}

export default function MedicalBusinessSchema({
  location,
  siteName,
  domain,
  paymentsAccepted = [],
  areaServed = [],
  doctors = [],
  hasProducts = false,
}: MedicalBusinessSchemaProps): ReactElement {
  const [state] = location.state.split('-');

  // Convert hours to schema.org text format
  const openingHours = Object.entries(location.hours)
    .map(([day, data]) => {
      if (data.open === 'Closed' || data.close === 'Closed') {
        return null;
      }
      const dayAbbrev = day.substring(0, 2);
      const openTime = convertTime12to24(data.open).substring(0, 5); // Remove seconds
      const closeTime = convertTime12to24(data.close).substring(0, 5); // Remove seconds
      return `${dayAbbrev} ${openTime}-${closeTime}`;
    })
    .filter(Boolean);

  // Build address object
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const address: any = {
    '@type': 'PostalAddress',
    streetAddress: `${location.address1}${location.address2 ? ` ${location.address2}` : ''}`,
    addressLocality: location.city,
    addressRegion: state,
    postalCode: location.zipCode,
    addressCountry: 'US',
  };

  // Build location object
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const locationObject: any = {
    '@type': 'Place',
    name: location.name,
    address,
    telephone: structuredPhoneNumber(location.phoneNumber),
    ...(location.map && {
      geo: {
        '@type': 'GeoCoordinates',
        latitude: location.map.lat.toString(),
        longitude: location.map.lon.toString(),
      },
    }),
  };

  // Build parentOrganization object
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parentOrganization: any = {
    '@type': 'Organization',
    name: siteName,
    url: `https://${domain}`,
  };

  // Build doctors array if available
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const employees: any[] | undefined =
    doctors.length > 0
      ? doctors.map((doctor) => ({
          '@type': 'Person',
          name: `${doctor.fields.firstName} ${doctor.fields.lastName}`,
          ...(doctor.fields.npi && {
            identifier: {
              '@type': 'PropertyValue',
              propertyID: 'NPI',
              value: String(doctor.fields.npi),
            },
          }),
          ...(doctor.fields.typeOfDoctor && {
            jobTitle: doctor.fields.typeOfDoctor,
          }),
        }))
      : undefined;

  const hasDoctors = doctors.length > 0;

  // Determine schema type based on business function:
  // - Optician: Store first (products) with no doctors
  // - Optometric: Medical services first (doctors) that also sell glasses (products)
  // - MedicalBusiness: Fallback for locations without doctors or products
  const schemaType =
    hasDoctors && hasProducts
      ? 'Optometric' // Medical services + products (glasses)
      : hasProducts && !hasDoctors
      ? 'Optician' // Store first (products only)
      : 'MedicalBusiness'; // Fallback
  const schemaId = `https://${domain}/locations/${location.slug}#${schemaType.toLowerCase()}`;

  return (
    <SchemaOrg
      json={{
        '@context': 'https://schema.org',
        '@type': schemaType,
        '@id': schemaId,
        name: location.name,
        url: `https://${domain}/locations/${location.slug}`,
        ...(openingHours.length > 0 && { openingHours }),
        ...(paymentsAccepted.length > 0 && { paymentAccepted: paymentsAccepted }),
        location: locationObject,
        ...(areaServed.length > 0 && { areaServed }),
        parentOrganization,
        ...(location.officePhoto?.fields.file.url && {
          image: `https:${location.officePhoto.fields.file.url}`,
        }),
        ...(employees && { employee: employees }),
      }}
    />
  );
}
